import React from 'react';
import InstagramPreview from './InstagramPreview';
import TikTokPreview from './TikTokPreview';
import BannerPreview from './BannerPreview';
import ProductPreview from './ProductPreview';
import ArticlePreview from './ArticlePreview';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getMediaTypeFromUrl } from '../../../utils/helpers';

// Generic Preview Component for platforms without specific preview
const GenericPreview = ({ platform, contentType, imageUrl, caption, specifications }) => {
  const { isRTL } = useLanguage();
  
  const platformNames = {
    snapchat: { en: 'Snapchat', ar: 'سناب شات', icon: '👻', color: 'bg-yellow-400' },
    twitter: { en: 'X (Twitter)', ar: 'إكس', icon: '𝕏', color: 'bg-black' },
    facebook: { en: 'Facebook', ar: 'فيسبوك', icon: '📘', color: 'bg-blue-600' },
    linkedin: { en: 'LinkedIn', ar: 'لينكد إن', icon: '💼', color: 'bg-blue-700' },
    website: { en: 'Website', ar: 'موقع', icon: '🌐', color: 'bg-indigo-500' },
    blog: { en: 'Website', ar: 'موقع', icon: '🌐', color: 'bg-indigo-500' }, // Backward compatibility: blog now shows as website
    other: { en: 'Preview', ar: 'معاينة', icon: '📋', color: 'bg-gray-500' },
  };
  
  const platformInfo = platformNames[platform] || platformNames.other;
  const aspectRatio = specifications?.aspect_ratio || '1:1';
  
  // Get aspect ratio class
  const getAspectClass = () => {
    switch (aspectRatio) {
      case '9:16': return 'aspect-[9/16]';
      case '16:9': return 'aspect-video';
      case '4:5': return 'aspect-[4/5]';
      case '1.91:1': return 'aspect-[1.91/1]';
      case '3:1': return 'aspect-[3/1]';
      case '4:1': return 'aspect-[4/1]';
      case '2:1': return 'aspect-[2/1]';
      default: return 'aspect-square';
    }
  };

  return (
    <div className="w-full max-w-[320px] mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
        {/* Platform Header */}
        <div className={`${platformInfo.color} px-4 py-3 flex items-center gap-2`}>
          <span className="text-2xl">{platformInfo.icon}</span>
          <span className="text-white font-semibold">
            {isRTL ? platformInfo.ar : platformInfo.en}
          </span>
        </div>
        
        {/* Content Area */}
        <div className={`bg-gray-100 ${getAspectClass()}`}>
          {imageUrl ? (
            getMediaTypeFromUrl(imageUrl) === 'video' ? (
              <video
                src={imageUrl}
                className="w-full h-full object-cover"
                controls
                muted
                playsInline
              />
            ) : (
              <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <div className="text-center p-4">
                <span className="text-5xl block mb-2">{platformInfo.icon}</span>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'معاينة المحتوى' : 'Content Preview'}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {specifications?.width && specifications?.height 
                    ? `${specifications.width} × ${specifications.height}`
                    : aspectRatio
                  }
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Caption Area */}
        {caption && (
          <div className="p-4 border-t border-gray-100">
            <p className="text-sm text-gray-700 line-clamp-3">{caption}</p>
          </div>
        )}
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">
        {isRTL ? platformInfo.ar : platformInfo.en} Preview
      </p>
    </div>
  );
};

// Main Platform Preview Component
const PlatformPreview = ({ 
  platform, 
  contentType, 
  imageUrl, 
  caption,
  username,
  title,
  description,
  richDescription,
  productDetails,
  articleDetails,
  specifications = {},
  carouselItems = []
}) => {
  const { isRTL } = useLanguage();
  
  if (!platform || !contentType) {
    return (
      <div className="w-full max-w-[320px] mx-auto p-8 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
        <span className="text-4xl block mb-2">👀</span>
        <p className="text-gray-500">
          {isRTL 
            ? 'اختر المنصة ونوع المحتوى لمشاهدة المعاينة' 
            : 'Select platform and content type to see preview'
          }
        </p>
      </div>
    );
  }

  // Route to specific preview components based on platform and content type
  switch (platform) {
    case 'instagram':
      return (
        <InstagramPreview
          contentType={contentType}
          imageUrl={imageUrl}
          caption={caption}
          username={username}
          aspectRatio={specifications.aspect_ratio}
          slidesCount={specifications.slides_count || (carouselItems && carouselItems.length > 0 ? carouselItems.length : 1)}
          carouselItems={carouselItems}
        />
      );
    
    case 'tiktok':
      return (
        <TikTokPreview
          imageUrl={imageUrl}
          caption={caption}
          username={username}
          duration={specifications.duration_seconds}
        />
      );
    
    case 'snapchat':
      // Use generic preview for Snapchat (can be enhanced later)
      return (
        <GenericPreview
          platform="snapchat"
          contentType={contentType}
          imageUrl={imageUrl}
          caption={caption}
          specifications={specifications}
        />
      );
    
    case 'website':
      // Route to specialized previews for website content types
      if (contentType === 'product') {
        return (
          <ProductPreview
            imageUrl={imageUrl}
            title={title}
            caption={caption}
            richDescription={richDescription}
            productDetails={productDetails}
            specifications={specifications}
          />
        );
      }
      
      if (contentType === 'blog') {
        return (
          <ArticlePreview
            imageUrl={imageUrl}
            title={title}
            caption={caption}
            description={description}
            richDescription={richDescription}
            articleDetails={articleDetails}
            specifications={specifications}
          />
        );
      }
      
      if (contentType === 'banner' || contentType === 'hero' || contentType === 'header') {
        return (
          <BannerPreview
            imageUrl={imageUrl}
            title={title}
            caption={caption}
            specifications={specifications}
          />
        );
      }
      // Fall through to generic preview for other website content types
    
    case 'linkedin':
    case 'facebook':
      // Use banner preview for banner content type
      if (contentType === 'banner') {
        return (
          <BannerPreview
            imageUrl={imageUrl}
            title={title}
            caption={caption}
            specifications={specifications}
          />
        );
      }
      // Fall through to generic preview
    
    // Other platforms use generic preview
    default:
      return (
        <GenericPreview
          platform={platform}
          contentType={contentType}
          imageUrl={imageUrl}
          caption={caption}
          specifications={specifications}
        />
      );
  }
};

export default PlatformPreview;
export { InstagramPreview, TikTokPreview, GenericPreview, BannerPreview, ProductPreview, ArticlePreview };





